import _ from 'lodash';
import React, { useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import styled from 'styled-components';
import { FieldProps } from 'formik';

import FormControl from '@material-ui/core/FormControl';

import { FormHelperText, FormLabel } from '../_ui/forms.component';
import { IntlMsg } from '../IntlMsg/intl-msg.component';
import { borderColor, mainColor } from '../../../lib/theme';

// Label is a plain string (rendered in <FormLabel>) or an IntlMsg-shaped
// descriptor with an `id` (rendered through IntlMsg) — a union, not `any`.
type RichTextEditorLabel = string | { id: string; defaultMessage?: string; values?: Record<string, unknown> };

// FieldProps<string> gives precise `field`/`form` types (sibling Formik
// components still use `any` — tighten them when next touched).
interface IFormikRichTextEditorProps extends FieldProps<string> {
  id?: string;
  fid?: string;
  label?: RichTextEditorLabel;
  placeholder?: string;
  minHeight?: string;
  // Instance ref to the underlying ReactQuill class component, whose
  // getEditor(): Quill supports caret reads and programmatic insertion.
  quillRef?: React.Ref<ReactQuill>;
  // Mirrors Quill's selection-change stream. The range is null whenever the
  // editor loses focus — callers needing the caret retain the last non-null value.
  onSelectionCapture?: (range: ReactQuill.Range) => void;
  onFocus?: () => void;
  // Locks the editor (e.g. while a submit is in flight) without swapping it for
  // a separate read-only view — the value stays bound and edits are blocked.
  readOnly?: boolean;
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ],
  clipboard: { matchVisual: false }
};

// Quill 2.x folded bullet lists into the single `list` format; listing `bullet`
// here throws "Cannot register bullet". The toolbar's { list: 'bullet' } still works.
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

const EditorWrapper = styled.div<{ minHeight?: string; hasError?: boolean }>`
  .quill {
    border-radius: 4px;
    background: #fff;
    overflow: hidden;
    border: 1px solid ${props => props.hasError ? '#ed4337' : borderColor};

    &:focus-within {
      border-color: ${mainColor};
    }
  }

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid ${borderColor};
    background: #fafcfe;
  }

  .ql-container.ql-snow {
    border: none;
    font-family: Quicksand, sans-serif;
    font-size: 14px;
  }

  .ql-editor {
    min-height: ${props => props.minHeight || '180px'};
  }
`;

export const FormikRichTextEditor: React.FC<IFormikRichTextEditorProps> = ({
  field,
  form,
  fid,
  id,
  label,
  placeholder,
  minHeight,
  quillRef,
  onSelectionCapture,
  onFocus,
  readOnly
}) => {
  const _id = id || fid;
  const error = _.get(form, `errors.${field.name}`);
  const touched = _.get(form, `touched.${field.name}`);
  const hasError = !!error && !!touched;

  let labelOutput: React.ReactNode = null;
  if (typeof label === 'object' && label !== null) {
    labelOutput = <FormLabel htmlFor={_id}><IntlMsg {...label} /></FormLabel>;
  } else if (typeof label === 'string') {
    labelOutput = <FormLabel htmlFor={_id}>{label}</FormLabel>;
  }

  // Quill emits the HTML string directly — store it on bodyHtml; the parent
  // form is responsible for deriving a plaintext `body` for SMS/preview.
  const handleChange = useCallback((html: string) => {
    form.setFieldValue(field.name, html);
  }, [form, field.name]);

  const handleBlur = useCallback(() => {
    form.setFieldTouched(field.name, true);
  }, [form, field.name]);

  return (
    <FormControl margin="dense" fullWidth component="div" error={hasError}>
      {labelOutput}
      <EditorWrapper minHeight={minHeight} hasError={hasError}>
        <ReactQuill
          ref={quillRef}
          id={_id}
          theme="snow"
          value={field.value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          onChangeSelection={onSelectionCapture}
          onFocus={onFocus}
          readOnly={readOnly}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder={placeholder}
        />
      </EditorWrapper>
      {hasError && _.isString(error) && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
